import { FC, useEffect } from 'react';
import { KeyedMutator } from 'swr';

import { PinPanelModal } from '../../components/pin/PinPanel';
import { getLogger } from '../../utils/logger/logger';
import { isOneOf } from '../../utils/types/typeUtils';

import { MediaMetadata } from './components/MediaMetadata';
import { ModalFullscreenPlayer } from './components/ModalFullscreenPlayer';
import { ProviderVariantModal } from './components/ProviderVariantModal';
import { ScpHead } from './components/ScpHead';
import { useContentStackEventHandlers } from './hooks/useContentStackEventHandlers';
import { SuperCoverPageSeries } from './contentStack.types';
import { SectionRenderer } from './SectionRenderer';

import styles from './StackedEpisodeOrMoviePage.module.css';

const logger = getLogger('[stacked-series]');

interface StackedSeriesProps {
  data: SuperCoverPageSeries;
  dataMutator: KeyedMutator<SuperCoverPageSeries>;
}
export const StackedSeriesPage: FC<StackedSeriesProps> = ({ data, dataMutator }) => {
  const { series } = data;
  const showVariants = series.defaultProviderVariant?.behavior === 'ShowVariants';
  const {
    // state
    assetId,
    isVariantModalOpen,
    // handlers
    onClickPrimaryAction,
    onSelectProvider,
    onCloseModalPlayer,
    onCloseVariantModal,
    selectedVariant,
    isPinModalOpen,
    onCloseParentalModal,
    onAccessGranted,
  } = useContentStackEventHandlers(series.defaultProviderVariant, dataMutator);

  useEffect(() => logger.log('series', series), [series]);

  return (
    <div className={styles.movieCoverPage}>
      <ScpHead scpData={data} />
      <MediaMetadata media={series} onClickPrimaryAction={onClickPrimaryAction} />
      <SectionRenderer sections={series.sections} onSelectProvider={onSelectProvider} />
      {/* modals */}
      {showVariants && (
        <ProviderVariantModal
          stack={series}
          isOpen={isVariantModalOpen}
          onSelectProvider={onSelectProvider}
          onClose={onCloseVariantModal}
        />
      )}
      {selectedVariant && isPinModalOpen && (
        <PinPanelModal
          context={
            isOneOf(selectedVariant.behavior, ['RentAsset', 'PurchaseAsset'])
              ? 'transactionControl'
              : 'parentalControls'
          }
          assetId={selectedVariant.tracking.elementId}
          isOpen={isPinModalOpen}
          onClose={onCloseParentalModal}
          ageRestriction={series.metadata.ageLimit}
          onAccessGranted={() => {
            onAccessGranted();
          }}
        />
      )}
      <ModalFullscreenPlayer assetId={assetId} onClose={onCloseModalPlayer} />
    </div>
  );
};
